import React, { useState } from "react";
import { KeyRound, PlugZap, Trash2, Loader2, CheckCircle2, XCircle, Lock, Star } from "lucide-react";
import { Badge } from "../Primitives";
import { statusOf, relTime } from "./shared";
import { testService, clearService } from "../../lib/integrationsApi";
import { cn } from "../../lib/utils";

const btn = "inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border rounded-sm transition-colors disabled:opacity-40";

export default function ServiceCard({ service: s, canEdit, actor, onEdit, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState(null);
  const st = statusOf(s);
  const id = s.service;

  async function run(kind, fn) {
    setBusy(kind); setErr(null);
    try { await fn(); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(null); setConfirm(false); }
  }

  return (
    <div data-testid={`service-card-${id}`}
         className={cn("tile p-4 flex flex-col gap-3 transition-transform duration-200 hover:-translate-y-0.5",
                       s.is_active_map && "ring-1 ring-primary/60")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display font-semibold text-[15px] truncate">{s.label}</span>
            {s.custom && <Badge status="neutral">custom</Badge>}
            {s.is_active_map && <Badge status="info" testid={`service-active-${id}`}><Star size={9} /> active map</Badge>}
          </div>
          <p className="text-[11.5px] text-muted mt-1 leading-relaxed">{s.description || "—"}</p>
        </div>
        <Badge status={st.status} testid={`service-status-${id}`}>{st.label}</Badge>
      </div>

      {s.fields.length > 0 && (
        <div className="space-y-1">
          {s.fields.map(f => (
            <div key={f.name} className="flex items-center justify-between text-[11px] border-b border-border/60 pb-1">
              <span className="text-muted">{f.label}</span>
              <span data-testid={`service-masked-${id}-${f.name}`} className="font-mono-tabular text-fg/90">
                {f.configured ? f.masked : <span className="text-subtle italic">not set</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {s.test && (
        <div data-testid={`service-test-${id}`}
             className={cn("text-[10.5px] flex items-start gap-1.5 leading-snug", s.test.ok ? "text-success" : "text-critical")}>
          {s.test.ok ? <CheckCircle2 size={11} className="mt-0.5 shrink-0" /> : <XCircle size={11} className="mt-0.5 shrink-0" />}
          <span>{s.test.detail} <span className="text-subtle">· {relTime(s.test.at)}</span></span>
        </div>
      )}
      {err && <div data-testid={`service-error-${id}`} className="text-[10.5px] text-critical">{err}</div>}

      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <span className="text-[10px] text-subtle font-mono-tabular truncate">
          {s.updated_at ? `${s.updated_by || "—"} · ${relTime(s.updated_at)}` : s.free ? "no key needed" : "never set"}
        </span>
        {s.free ? null : canEdit ? (
          <div className="flex items-center gap-1.5 shrink-0">
            {s.configured && (
              <button data-testid={`service-test-btn-${id}`} disabled={!!busy} onClick={() => run("test", () => testService(id, actor))}
                      className={cn(btn, "border-border text-muted hover:text-primary hover:border-primary")}>
                {busy === "test" ? <Loader2 size={11} className="animate-spin" /> : <PlugZap size={11} />} Test
              </button>
            )}
            {s.configured && (confirm ? (
              <button data-testid={`service-clear-confirm-${id}`} disabled={!!busy}
                      onClick={() => run("clear", () => clearService(id, actor))}
                      className={cn(btn, "border-critical text-critical bg-critical/10")}>
                {busy === "clear" ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Confirm
              </button>
            ) : (
              <button data-testid={`service-clear-btn-${id}`} disabled={!!busy} onClick={() => setConfirm(true)}
                      className={cn(btn, "border-border text-muted hover:text-critical hover:border-critical")}>
                <Trash2 size={11} /> {s.custom ? "Delete" : "Clear"}
              </button>
            ))}
            <button data-testid={`service-edit-btn-${id}`} disabled={!!busy} onClick={() => onEdit(s)}
                    className={cn(btn, "border-primary/50 text-primary hover:bg-primary/10")}>
              <KeyRound size={11} /> {s.configured ? "Update" : "Add key"}
            </button>
          </div>
        ) : (
          <span className="text-[10px] text-subtle inline-flex items-center gap-1"><Lock size={10} /> read-only</span>
        )}
      </div>
    </div>
  );
}
